import { useNavigate } from '@tanstack/react-router';
import type { IdentifiableTItem, PublishedTeam } from '@tempo-io/roadmap-dto';
import { addMonths, format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { PublishedReadOnlyItemPanel } from '@/components/modular-item-panel/PublishedReadOnlyItemPanel';
import { type ItemV2, SchedulingType } from '@/lib/api/types/items';
import type {
  ItemStatusOption,
  ItemTypeLevelOption,
  ItemTypeOption,
} from '@/lib/api/types/loop-visualization';
import type { Team } from '@/lib/api/types/teams';
import { useItemPanelNavigation } from '@/lib/hooks/item-panel/useItemPanelParams';
import { Timeline } from '../timeline/Timeline';
import { ViewContextProvider } from '../ViewContext';
import type { PublishedTimelineResponse } from './types';

function dtoItemToItem(
  item: IdentifiableTItem,
  itemDetails: PublishedTimelineResponse['itemDetails'][string] | undefined,
): ItemV2 {
  return {
    id: item.id,
    name: item.title,
    description: item.description ?? null,
    itemTypeId: item.itemTypeId,
    itemStatusId: item.itemStatusId ?? 0,
    parentId: item.parentId != null ? Number(item.parentId) : null,
    teamId: item.teamId ?? null,
    earliestStartDateTime: null,
    deadlineDateTime: null,
    plannedStartDateTime: item.startDate || null,
    plannedFinishDateTime: item.endDate || null,
    schedulingType: SchedulingType.PLANNED_AND_ACTUALS,
    createdAt: itemDetails?.createdAt ?? '',
    updatedAt: itemDetails?.updatedAt ?? '',
    createdBy: itemDetails?.createdBy ?? '',
    updatedBy: itemDetails?.updatedBy ?? '',
    customFieldValues: (itemDetails?.customFields ?? {}) as ItemV2['customFieldValues'],
    priority: '',
    dependencies: itemDetails?.dependencies ?? [],
    timeSpentSeconds: null,
    doneDateTime: itemDetails?.doneDateTime ?? null,
    externalKey: null,
    externalLink: null,
    estimationId: itemDetails?.estimationId ?? null,
    loopId: 0,
  };
}

interface PublishedTimelineInnerProps {
  data: PublishedTimelineResponse;
  searchParams: { itemId?: number };
  showLegend: boolean;
  isExportMode?: boolean;
  hideLegendOverlay?: boolean;
}

function PublishedTimelineInner({
  data,
  searchParams,
  showLegend,
  isExportMode,
  hideLegendOverlay,
}: PublishedTimelineInnerProps) {
  const { closeItemPanel } = useItemPanelNavigation();
  const selectedItemId = searchParams.itemId != null ? String(searchParams.itemId) : null;
  const [expanded, setExpanded] = useState(false);

  const items = useMemo<ItemV2[]>(
    () => data.items.map((item) => dtoItemToItem(item, data.itemDetails[String(item.id)])),
    [data.itemDetails, data.items],
  );

  const itemTypes = useMemo<ItemTypeOption[]>(
    () =>
      data.itemTypes.map((t) => ({
        id: t.id,
        name: t.name,
        itemLevelId: t.itemLevelId,
        color: t.color ?? null,
        icon: t.icon ?? null,
      })),
    [data.itemTypes],
  );

  const itemLevels = useMemo<ItemTypeLevelOption[]>(
    () =>
      data.itemLevels.map((l) => ({
        id: l.id,
        level: l.level,
        name: l.name,
      })),
    [data.itemLevels],
  );

  const itemStatuses = useMemo<ItemStatusOption[]>(
    () =>
      data.itemStatuses.map((s) => ({
        id: s.id,
        name: s.name,
        color: s.color,
        schedulingType: 'IN_PROGRESS' as const,
      })),
    [data.itemStatuses],
  );

  const teams = useMemo<Team[]>(
    () =>
      data.teams.map((t: PublishedTeam) => ({
        id: t.id,
        name: t.name,
        color: t.color,
        totalCapacitySeconds: null,
        createdAt: '',
        updatedAt: '',
        description: null,
      })),
    [data.teams],
  );

  const itemsById = useMemo<Record<string, IdentifiableTItem>>(() => {
    const map: Record<string, IdentifiableTItem> = {};
    for (const item of data.items) {
      map[String(item.id)] = item;
    }
    return map;
  }, [data.items]);

  const publishedItemRanks = useMemo<Map<number, string>>(() => {
    const map = new Map<number, string>();
    data.items.forEach((item, index) => {
      map.set(item.id, String(index).padStart(10, '0'));
    });
    return map;
  }, [data.items]);

  const navigate = useNavigate();

  const handleItemClick = useCallback(
    (itemId: number) => {
      navigate({ to: '.', search: (prev) => ({ ...prev, itemId }) });
      setExpanded(true);
    },
    [navigate],
  );

  const handleClose = useCallback(() => {
    setExpanded(false);
    closeItemPanel();
  }, [closeItemPanel]);

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const today = new Date();
  const from = data.layout.startDate ?? format(today, 'yyyy-MM-dd');
  const to = data.layout.endDate ?? format(addMonths(today, 6), 'yyyy-MM-dd');

  return (
    <>
      <Timeline
        items={items}
        itemTypes={itemTypes}
        itemLevels={itemLevels}
        itemStatuses={itemStatuses}
        layout={data.layout}
        visualizationId={data.layout.visualizationId}
        hasFilters={false}
        from={from}
        to={to}
        onDateChange={() => undefined}
        onItemClick={handleItemClick}
        canEdit={false}
        viewLocked={true}
        isPublishedMode={true}
        isExportMode={isExportMode}
        publishedLegendItems={showLegend ? data.legendItems : undefined}
        publishedItemRanks={publishedItemRanks}
        mainHeaderConfig={data.mainHeaderConfig}
        subHeaderConfigs={data.subHeaderConfigs}
        publishedHeaderDisplayValuesByHeaderId={data.headerDisplayValuesByHeaderId}
        teamsOverride={teams}
        hideLegendOverlay={hideLegendOverlay}
      />
      <PublishedReadOnlyItemPanel
        itemDetails={selectedItemId ? (data.itemDetails[selectedItemId] ?? null) : null}
        enrichedItem={selectedItemId ? (itemsById[selectedItemId] ?? null) : null}
        allItemDetails={data.itemDetails}
        enrichedItems={itemsById}
        customFieldDefinitions={data.customFieldDefinitions}
        hiddenFieldIds={data.hiddenFieldIds}
        customerLookup={data.customerLookup}
        expanded={expanded}
        onToggle={handleToggle}
        onClose={handleClose}
      />
    </>
  );
}

export function PublishedTimeline({
  data,
  searchParams,
  showLegend,
  isExportMode,
  hideLegendOverlay,
}: {
  data: PublishedTimelineResponse;
  searchParams: { itemId?: number };
  showLegend: boolean;
  isExportMode?: boolean;
  hideLegendOverlay?: boolean;
}) {
  return (
    <ViewContextProvider
      viewId={data.metadata.viewId}
      loopId={data.layout.visualizationId}
      isPublishedMode={true}
    >
      <PublishedTimelineInner
        data={data}
        searchParams={searchParams}
        showLegend={showLegend}
        isExportMode={isExportMode}
        hideLegendOverlay={hideLegendOverlay}
      />
    </ViewContextProvider>
  );
}
